import {
  createContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef
} from 'react'
import { useQuery } from '@apollo/client'
import { useRouter } from 'next/router'
import { useAuth } from '../../../utils/auth'
import { COMMUNITY_PROFILE_QUERY } from '../../../gql'
import {
  LabWelcomeGuideStep,
  LabWelcomeGuideStepEnum
} from '../../../__generated__/graphql'
import type { CommunityProfile } from '../../../__generated__/graphql'
import OnboardProgress from './OnboardProgress'
import WelcomeModal from './WelcomeModal'
import CongratsModal from './CongratsModal'

export const PENDING_CONGRATS_STORAGE_KEY = 'labOnboardingPendingCongratsModal'
export const PENDING_CONGRATS_AFTER_NEXT_NAV = 'afterNextNavigation'

const TOTAL_STEPS = Object.keys(LabWelcomeGuideStepEnum).length

export type LabOnboardingContextValue = {
  userSteps: LabWelcomeGuideStep[]
  completedCount: number
  totalSteps: number
  isComplete: boolean
  loading: boolean
  communityProfile: CommunityProfile
  openWelcomeModal: () => void
  openCongratsModal: () => void
}

export const LabOnboardingContext = createContext<LabOnboardingContextValue>({
  userSteps: [],
  completedCount: 0,
  totalSteps: 0,
  isComplete: false,
  loading: false,
  communityProfile: null,
  openWelcomeModal: () => {},
  openCongratsModal: () => {}
})

const LabOnboardingProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const router = useRouter()
  const { currentUser } = useAuth()
  const [welcomeModalOpen, setWelcomeModalOpen] = useState(false)
  const [congratsModalOpen, setCongratsModalOpen] = useState(false)

  const initialPathRef = useRef<string | null>(null)
  const initialPendingCongratsRef = useRef<boolean | null>(null)

  const { data, loading } = useQuery(COMMUNITY_PROFILE_QUERY, {
    variables: { userId: currentUser!.id },
    skip: !currentUser?.id
  })

  const communityProfile = data?.communityProfile
    ?.communityProfile as CommunityProfile
  const userSteps = communityProfile?.welcomeGuideSteps ?? []
  const completedCount = userSteps.filter(
    (step) => step.completedAt != null
  ).length
  const isComplete = completedCount >= TOTAL_STEPS

  const openWelcomeModal = useCallback(() => setWelcomeModalOpen(true), [])
  const openCongratsModal = useCallback(() => setCongratsModalOpen(true), [])
  const closeWelcomeModal = useCallback(() => setWelcomeModalOpen(false), [])
  const closeCongratsModal = useCallback(() => setCongratsModalOpen(false), [])

  const handleAllStepsComplete = useCallback(() => {
    setWelcomeModalOpen(false)
    setCongratsModalOpen(true)
  }, [])

  useEffect(() => {
    if (!router.isReady) return

    if (initialPathRef.current === null) {
      initialPathRef.current = router.asPath
    }
  }, [router.asPath, router.isReady])

  useEffect(() => {
    if (loading || !isComplete) return

    const pendingCongratsValue = window.sessionStorage.getItem(
      PENDING_CONGRATS_STORAGE_KEY
    )
    const hasPendingCongrats =
      pendingCongratsValue === 'true' ||
      pendingCongratsValue === PENDING_CONGRATS_AFTER_NEXT_NAV

    if (initialPendingCongratsRef.current === null) {
      initialPendingCongratsRef.current = hasPendingCongrats
    }

    if (!hasPendingCongrats) return

    const hasNavigatedFromInitialPath =
      initialPathRef.current != null && router.asPath !== initialPathRef.current
    const hadPendingOnInitialLoad = initialPendingCongratsRef.current === true

    if (!hadPendingOnInitialLoad && !hasNavigatedFromInitialPath) {
      return
    }

    if (pendingCongratsValue === PENDING_CONGRATS_AFTER_NEXT_NAV) {
      window.sessionStorage.setItem(PENDING_CONGRATS_STORAGE_KEY, 'true')
      return
    }

    window.sessionStorage.removeItem(PENDING_CONGRATS_STORAGE_KEY)
    handleAllStepsComplete()
  }, [handleAllStepsComplete, isComplete, loading, router.asPath])

  const onboardingValue: LabOnboardingContextValue = useMemo(
    () => ({
      userSteps,
      completedCount,
      totalSteps: TOTAL_STEPS,
      isComplete,
      loading,
      communityProfile,
      openWelcomeModal,
      openCongratsModal
    }),
    [
      userSteps,
      completedCount,
      isComplete,
      loading,
      communityProfile,
      openWelcomeModal,
      openCongratsModal
    ]
  )

  return (
    <LabOnboardingContext.Provider value={onboardingValue}>
      {children}
      <OnboardProgress />
      {welcomeModalOpen && <WelcomeModal closeModal={closeWelcomeModal} />}
      {congratsModalOpen && <CongratsModal closeModal={closeCongratsModal} />}
    </LabOnboardingContext.Provider>
  )
}

export default LabOnboardingProvider
